import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { compose } from 'recompose';
import hash from 'object-hash';
import pageWithProps from 'shared/hoc/pageWithProps';
import { getDocumentByType } from 'shared/utils/documents';
import {
  H1,
  Error as BaseError,
  FlexColumn,
  StyledLink,
  TamplateField,
  SubmitButton,
  SmallText,
} from 'shared/components';
import { useForm, FormContext } from 'react-hook-form';
import Router, { withRouter } from 'next/router';
import { Loading } from 'shared/bo-components';
import { RichText } from 'prismic-reactjs';
import { apiPrefix } from 'shared/utils/api';
import { upToMedia } from 'shared/style/media';

const PageWrapper = styled(FlexColumn)`
  justify-content: center;
  align-items: center;
  height: 600px;
`;

const PageTitle = styled(H1)`
  text-align: center;
  margin-top: 15px;
`;

const FormWrapper = styled.form`
  display: flex;
  width: 700px;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;

  ${upToMedia.bigMobile`
  width: 90%
`}
`;

const Link = styled(StyledLink)`
  color: ${({ theme }) => theme.palette.cyan};
  margin-top: 20px;
`;

const Error = styled(BaseError)`
  height: 20px;
  margin-bottom: 15px;
`;

const LoadingWrapper = styled.div`
  height: 20px;
  margin-top: 20px;
`;

const Instructions = styled(SmallText)`
  margin-top: 15px;
  width: 70%;
  max-width: 500px;
  text-align: center;
`;

const Login = ({ instructions }) => {
  const methods = useForm({
    mode: 'onBlur',
  });

  const { watch, triggerValidation, formState } = methods;

  const watchPassword = watch('password');

  const [loginError, setLoginError] = useState(null);

  const [isLoading, setIsLoading] = useState();

  const handleSubmit = useCallback(async ({ email, password }) => {
    setLoginError(null);
    const hashedPassword = hash({ password });
    const options = {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password: hashedPassword }),
    };
    setIsLoading(true);
    const res = await fetch(`${apiPrefix}/users/login-for-download`, options);
    if (res.status === 200) {
      const json = await res.json()
      if (json && json.name) {
        const name = json.name.split(' ').map(word => word[0]).join('').toUpperCase();
        localStorage.setItem('userName', name)
      } else {
        localStorage.setItem('userName', ' ')
      }
      return Router.pushRoute('/downloadarea');
    }
    setIsLoading(false);
    return setLoginError(res.status === 401 ? 'Wrong email or password' : 'Server Error');
  }, []);

  const handleFieldFocus = useCallback(() => {
    setLoginError(null);
  }, []);

  useEffect(() => {
    if (methods && methods.formState.dirty) {
      triggerValidation('password');
    }
  }, [watchPassword]);

  return (
    <PageWrapper>
      <PageTitle>Download Area Login</PageTitle>
      <Instructions>{RichText.asText(instructions)}</Instructions>
      <FormContext {...methods}>
        <FormWrapper onSubmit={methods.handleSubmit(handleSubmit)}>
          <TamplateField
            title="Email"
            component="EmailField"
            name="email"
            placeholder="Email"
            showTitle={false}
            onFocus={handleFieldFocus}
            required
          />
          <TamplateField
            title="Password"
            component="PasswordField"
            name="password"
            placeholder="Password"
            showTitle={false}
            onFocus={handleFieldFocus}
            noBottomMargin
            required
          />
          <Error>{loginError}</Error>
          <SubmitButton text="Login" disabled={!formState.isValid} />
          <Link route="reset-password">Resend my password</Link>
          <LoadingWrapper>{isLoading && <Loading text="Verifying user... " />}</LoadingWrapper>
        </FormWrapper>
      </FormContext>
    </PageWrapper>
  );
};

const enhance = compose(
  pageWithProps(async ({ lang, ref }) => ({
    ...(await getDocumentByType('login_page', lang, ref)),
  })),
  withRouter,
);

export default enhance(Login);
